import axios from 'axios';
import store from "@/store";

export const baseURL = process.env.VUE_APP_API_URL;

class ApiService {
    _axios;
    _isRefreshing = false;
    _refreshPromise = null;
    _refreshSubscribers = [];
    _refreshRejectors = [];

    constructor({ baseURL: customBaseURL } = {}) {
        this._axios = axios.create({
            baseURL: customBaseURL || baseURL,
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
                'ef-client-type': 'web'
            }
        });

        this._axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (
                    error.response?.status === 401 &&
                    !originalRequest._retry &&
                    !originalRequest.headers?.['ef-skip-refresh']
                ) {
                    originalRequest._retry = true;

                    if (!this._isRefreshing) {
                        this._isRefreshing = true;
                        this._refreshPromise = this._refreshToken()
                            .then(response => {
                                const { token, refreshToken } = response.data;
                                localStorage.setItem('token', token);
                                localStorage.setItem('refreshToken', refreshToken);
                                this._onRefreshed(token);
                                return { token, refreshToken };
                            })
                            .catch((error) => {
                                this._onRefreshFailed(error, originalRequest);
                                throw error;
                            })
                            .finally(() => {
                                this._isRefreshing = false;
                                this._refreshPromise = null;
                            });
                    }

                    return new Promise((resolve, reject) => {
                        this._subscribeTokenRefresh(
                            (token) => {
                                // deep copy, to ensure original request is not mutated
                                const retryRequest = {
                                    ...originalRequest,
                                    headers: { ...originalRequest.headers },
                                    data: originalRequest.data,
                                    transformResponse: axios.defaults.transformResponse, // Ensure JSON parsing
                                };
                                retryRequest.headers['Authorization'] = `Bearer ${token}`;
                                this._axios(retryRequest).then(resolve).catch(reject);
                            },
                            (error) => {
                                reject(error); // Reject this retry if refresh failed
                            }
                        );
                    });
                }

                return Promise.reject(error);
            }
        );
    }

    _subscribeTokenRefresh(resolve, reject) {
        this._refreshSubscribers.push(resolve);
        this._refreshRejectors.push(reject);
    }

    _onRefreshed(token) {
        this._refreshSubscribers.forEach((cb) => cb(token));
        this._clearSubscribers();
    }

    _clearSubscribers() {
        this._refreshSubscribers = [];
        this._refreshRejectors = [];
    }

    _onRefreshFailed(error, originalRequest) {
        this._refreshRejectors.forEach((cb) => cb(error));
        this._clearSubscribers();
        // if ef-skip-logout true then 401 response from guarded api, will not cause logout
        const skipLogout = originalRequest?.headers?.['ef-skip-logout'];
        if (!Boolean(skipLogout)) return this._logout();
    }

    async _refreshToken() {
        return axios.post(
            `${baseURL}/auth/web-refresh-token`,
            {},
            {
                withCredentials: true,
                headers: {
                    'ef-skip-refresh': 'true',
                    'Content-Type': 'application/json'
                }
            }
        );
    }

    async _logout() {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return store.dispatch('logout');
    }

    async get(url, config = {}) {
        return this._axios.get(url, config);
    }

    async post(url, body, config = {}) {
        return this._axios.post(url, body, config);
    }

    async put(url, body, config = {}) {
        return this._axios.put(url, body, config);
    }

    async delete(url, body = {}, config = {}) {
        return this._axios.delete(url, { ...config, data: body });
    }

    static getQueryString(params) {
        if (!params) return '';
        return Object.keys(params)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&');
    }
}

const apiService = new ApiService();
export default apiService;

